import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import {
  Brain, Loader2, Target, Users, Zap, DollarSign, Activity, AlertTriangle,
  ArrowRight, SlidersHorizontal, Table2, RefreshCw, Info, ChevronDown, ChevronUp,
  ClipboardCheck, BookOpen, Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import ConstraintContextBanner from '../components/impact/ConstraintContextBanner';
import { getLightSectionTheme } from '../lib/sectionThemes';

const LEI_MEASURES = [
  { id: 'training_scope', label: 'Training Scope Index', info: 'What % of your workforce is reached by training programs. Enter your current reach and the target after intervention.', source: 'LMS participation reports: unique learners / total headcount × 100. Format: 0-100%.' },
  { id: 'sme_training', label: 'SME Training Scope', info: 'Coverage of subject-matter-expert-led training. SME training has higher capability transfer rates because it is role-specific and practical.', source: 'LMS filtered by SME-tagged programs. SME participants / total headcount × 100. Format: 0-100%.' },
  { id: 'community_learning', label: 'Community Learning', info: 'Participation in peer learning, forums, and communities of practice. This primarily drives motivation — people who learn together apply more.', source: 'Collaboration platform analytics (Teams, Slack, Yammer) or LMS social learning reports. Active participants / total. Format: 0-100%.' },
  { id: 'voluntary_engagement', label: 'Voluntary Learning', info: 'The % of learners engaging voluntarily (not mandated). High voluntary engagement signals strong motivation and learning culture.', source: 'LMS: filter out mandatory/compliance enrollments. Voluntary completions / total workforce × 100. Format: 0-100%.' },
  { id: 'tech_utilization', label: 'Tech Utilization', info: 'Active usage of your learning platforms. Low utilization means content exists but isn\'t being accessed — a direct barrier to capability building.', source: 'LMS monthly active users / total licensed users × 100. Format: 0-100%.' },
  { id: 'learning_culture', label: 'Learning Culture', info: 'How strongly learning is valued organizationally. This is the single biggest driver of sustained motivation and long-term capability transfer.', source: 'Aggregate of culture survey items: voluntary engagement, manager support, peer support, leadership advocacy. Average of sub-scores. Format: 0-100%.' },
  { id: 'training_performance', label: 'Training Performance', info: 'Composite of assessment scores, pass rates, and mastery levels. This is your strongest L2 signal — did learners actually acquire the knowledge?', source: 'LMS: (0.4 × avg assessment score) + (0.3 × pass rate) + (0.3 × mastery index). Format: 0-100%.' },
  { id: 'nps', label: 'Learner NPS', info: 'Learner satisfaction and willingness to recommend. While not a capability measure, high NPS correlates with higher motivation to apply learning.', source: 'Post-training survey: "How likely are you to recommend this program?" (-100 to +100 NPS, converted to 0-100 scale: (NPS + 100) / 2). Format: 0-100.' },
  { id: 'skills_assessment', label: 'Skills Assessment', info: 'Pre/post assessment scores showing actual skill gain. This is the core L2 measure — the delta between pre and post directly measures capability change.', source: 'LMS assessment module: (0.3 × pre-score) + (0.4 × post-score) + (0.3 × uplift ratio). Format: 0-100%.' },
  { id: 'skills_coverage', label: 'Skills Coverage', info: 'How well your training covers required skills vs gaps. Enter current coverage and target. Closing skill gaps directly increases workforce capability.', source: 'Skills framework: (skills gap closure % + bench depth index) / 2. From your skills management system or competency framework. Format: 0-100%.' },
  { id: 'certification_depth', label: 'Certification Depth', info: 'Current and target certification rates. Certifications provide validated, measurable proof of capability — strong L2 evidence for stakeholders.', source: 'Certification system: (0.6 × current cert rate) + (0.2 × advanced cert rate) + (0.2 × renewal rate). Format: 0-100%.' },
  { id: 'readiness_index', label: 'Readiness Index', info: 'How quickly new/upskilled employees reach full competency. Faster readiness = faster ROI from learning investment.', source: 'HR/Onboarding system: (target time to competency / actual time to competency) × 100. If target is 90 days and actual is 120 days = 75%. Format: 0-100%.' },
  { id: 'cost_efficiency', label: 'Cost Efficiency', info: 'Learning cost relative to outcomes delivered. Improving efficiency doesn\'t build capability directly but frees budget for higher-impact programs.', source: 'Finance: (target cost per learner / actual cost per learner) × 100. Format: 0-100%.' },
  { id: 'scheduling_velocity', label: 'Scheduling Velocity', info: 'Speed and efficiency of training delivery. Faster delivery means capability reaches the workforce sooner, reducing time-to-impact.', source: 'Operations: (target delivery cost / actual delivery cost) × 100. Format: 0-100%.' },
  { id: 'kpi_alignment', label: 'KPI Alignment', info: 'How tightly your learning programs are aligned to business KPIs. This is the highest-leverage measure — perfectly aligned training drives the most ROI.', source: 'Calculated: sum of (LEI coefficient × KPI improvement) across all linked KPIs × 100. Typically derived from this platform\'s correlation analysis. Format: 0-100%.' },
];
const SIMPLE_IDS = ['training_performance', 'skills_assessment', 'skills_coverage', 'certification_depth', 'readiness_index'];

// ──── Section narrative explanations ────
const SECTION_NARRATIVES = {
  lei: {
    simple: 'These 5 measures capture the operational effectiveness of your learning system. Improvements here drive the Capability and Motivation components of the People Engine — they are the starting point of the entire ROI waterfall.',
    advanced: 'Enter your current baseline and improvement target for each of the 15 LEI measures. The delta between current and target determines how much learning system improvement flows into the PE equation.',
  },
  capability: {
    subtitle: 'Determines the "C" in C × M = PE',
    narrative: 'These inputs set the Capability (C) score — the knowledge and skill dimension of the People Engine. Higher capability means learners have acquired more skills, but capability alone doesn\'t drive performance. It must be multiplied by Motivation (M) to predict PE, then validated by ROE to prove real-world impact.',
    impact: 'Every +1% in Capability multiplies directly with Motivation to increase PE Ideal. For example: C at 60% × M at 70% = PE Ideal of 42%. Raising C to 75% would lift PE Ideal to 52.5% — a 25% improvement in predicted workforce capacity.',
  },
  motivation: {
    subtitle: 'Determines the "M" in C × M = PE',
    narrative: 'These inputs set the Motivation (M) score — the willingness and confidence dimension. People may know what to do (C), but without motivation, they won\'t change behavior. Manager support is the single most powerful lever here — research shows it accounts for 80-90% of training transfer.',
    impact: 'Motivation is the multiplier. Even if Capability is high at 80%, low Motivation of 40% means PE Ideal is only 32%. Raising Motivation to 70% would more than double PE to 56%. This is often the most cost-effective area to improve.',
  },
  l3: {
    subtitle: 'Carries 60% weight in the ROE score',
    narrative: 'L3 (Kirkpatrick Level 3) measures whether learning actually changed on-the-job behavior. This is the critical validation step — the "missing link" between predicted capability and real business impact. L3 carries 60% of the ROE weight because behavior change is the most reliable predictor of KPI movement.',
    impact: 'L3 directly determines your ROE score. ROE acts as a reality multiplier: PE Validated = PE Ideal × ROE. If PE Ideal is 55% but ROE is only 60%, your validated capacity drops to 33%. Improving L3 has the highest leverage of any single input on your final ROI.',
  },
  financial: {
    narrative: 'The cost per employee is used to calculate your net ROI. The model generates a dollar value per employee from projected KPI improvements, then subtracts your intervention cost to determine return.',
  },
};

// ──── Cause & Effect Flow Diagram ────
const ROE_WATERFALL_EXPANDED_KEY = 'roe_modeller_waterfall_expanded';
const ROE_WATERFALL_OPEN_COUNT_KEY = 'roe_modeller_waterfall_open_count';
const ROE_WATERFALL_BADGE_THRESHOLD = 3;

function CauseEffectDiagram() {
  // Default to expanded on first visit; remember user's choice thereafter.
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(ROE_WATERFALL_EXPANDED_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  // Track how many times this page has been opened; badge auto-dismisses after threshold.
  const [openCount, setOpenCount] = useState(() => {
    try { return parseInt(localStorage.getItem(ROE_WATERFALL_OPEN_COUNT_KEY) || '0', 10) || 0; }
    catch { return 0; }
  });

  useEffect(() => {
    try {
      const next = openCount + 1;
      localStorage.setItem(ROE_WATERFALL_OPEN_COUNT_KEY, String(next));
      setOpenCount(next);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showRecommendedBadge = openCount < ROE_WATERFALL_BADGE_THRESHOLD;

  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem(ROE_WATERFALL_EXPANDED_KEY, String(next)); } catch { /* noop */ }
      return next;
    });
  };
  const steps = [
    { id: 'lei', label: 'LEI', sublabel: 'Learning\nImprovement', color: 'bg-slate-700', question: 'How much did learning improve?', detail: 'Your slider inputs feed LEI scores measuring training quality, reach, and effectiveness.' },
    { id: 'cm', label: 'C × M', sublabel: 'Capability ×\nMotivation', color: 'bg-blue-600', question: 'What skills & motivation changed?', detail: 'LEI improvements map to Capability (C) and Motivation (M) through elasticity coefficients.' },
    { id: 'pe', label: 'PE Ideal', sublabel: 'People Engine\n(Predicted)', color: 'bg-purple-500', question: 'What is your predicted workforce capacity?', detail: 'PE Ideal = C × M. This is your projected workforce performance — before real-world validation.' },
    { id: 'roe', label: 'ROE', sublabel: 'Return on\nExpectation', color: 'bg-teal-600', question: 'Did capability become behavior?', detail: 'ROE = (0.4 × L2 Performance Shift) + (0.6 × L3 Stakeholder Impact). The reality multiplier.' },
    { id: 'pev', label: 'PE Valid', sublabel: 'Validated\nCapacity', color: 'bg-purple-700', question: 'What capacity was actually transferred?', detail: 'PE Validated = PE Ideal × ROE. This is your proven workforce performance after behavior validation.' },
    { id: 'kpi', label: 'KPI', sublabel: 'Business\nMetrics', color: 'bg-emerald-600', question: 'Which business metrics moved?', detail: 'Validated PE maps to business KPIs (productivity, quality, safety, CSAT) through elasticity modeling.' },
    { id: 'roi', label: 'ROI', sublabel: 'Financial\nReturn', color: 'bg-amber-600', question: 'What is the dollar value and return?', detail: 'Total KPI value per employee minus your intervention cost = Net ROI. The bottom line.' },
  ];

  return (
    <Card className="border-slate-200 bg-gradient-to-b from-slate-50 to-white" data-testid="cause-effect-diagram">
      <CardContent className="pt-4 pb-3">
        <div className="w-full flex items-center justify-between gap-3">
          <button onClick={toggleExpanded} className="flex items-center gap-2 group flex-1 min-w-0 text-left" data-testid="cause-effect-toggle">
            <Info className="h-4 w-4 text-teal-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-slate-800 truncate">How the ROE Model Works — The Learning ROI Waterfall</span>
            {showRecommendedBadge && (
              <Badge
                className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 gap-1 px-1.5 py-0 text-[10px] font-semibold flex-shrink-0"
                data-testid="roe-waterfall-recommended-badge"
              >
                <Sparkles className="h-2.5 w-2.5" /> Recommended
              </Badge>
            )}
            {expanded
              ? <ChevronUp className="h-4 w-4 text-slate-400 group-hover:text-slate-600 ml-auto flex-shrink-0" />
              : <ChevronDown className="h-4 w-4 text-slate-400 group-hover:text-slate-600 ml-auto flex-shrink-0" />}
          </button>
          <Link
            to="/roe-reference#waterfall"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:text-teal-900 hover:underline flex-shrink-0"
            data-testid="roe-waterfall-reference-link"
          >
            <BookOpen className="h-3.5 w-3.5" /> Reference
          </Link>
        </div>

        {expanded && (
          <div className="mt-4 space-y-5" data-testid="cause-effect-content">
            <p className="text-sm text-slate-600 leading-relaxed">
              The ROI Waterfall connects every learning dollar to the business outcomes it produces.
              Each stage below is quantified — your slider inputs enter at <strong>LEI</strong> and flow right through to <strong>ROI</strong>.
              The key insight: <em>Capability alone doesn't drive performance.</em> ROE validates whether predicted capacity actually translates into behavior change and business impact.
            </p>

            {/* Flow Diagram */}
            <div className="overflow-x-auto pb-2">
              <div className="flex items-start gap-1 min-w-[700px]">
                {steps.map((step, i) => (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div className={cn("w-full rounded-lg py-2 px-1 text-center text-white shadow-sm", step.color)}>
                        <p className="text-xs font-bold">{step.label}</p>
                      </div>
                      <div className="mt-2 text-center px-1">
                        <p className="text-[10px] text-slate-500 font-medium whitespace-pre-line leading-tight">{step.sublabel}</p>
                        <p className="text-[10px] text-slate-400 mt-1 italic">{step.question}</p>
                      </div>
                      <div className="mt-1.5 px-1 text-center">
                        <p className="text-[10px] text-slate-500 leading-snug">{step.detail}</p>
                      </div>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="flex items-center pt-2.5 flex-shrink-0">
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Key Equations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs font-bold text-blue-800">PE Ideal = C × M</p>
                <p className="text-[10px] text-blue-600 mt-0.5">Capability multiplied by Motivation predicts workforce performance potential — before real-world validation.</p>
              </div>
              <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg">
                <p className="text-xs font-bold text-teal-800">ROE = 0.4 × LPS + 0.6 × SI</p>
                <p className="text-[10px] text-teal-600 mt-0.5">Learner Performance Shift (L2) and Stakeholder Impact (L3) combine to form the reality multiplier on PE.</p>
              </div>
              <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <p className="text-xs font-bold text-purple-800">PE Validated = PE Ideal × ROE</p>
                <p className="text-[10px] text-purple-600 mt-0.5">Your proven workforce capacity. This is what actually drives KPI movement and determines financial ROI.</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
              <strong>What to model:</strong> Adjust the sliders below to simulate a learning intervention. The model calculates how your improvements flow through each stage of the waterfall to produce a projected ROE, PE Validated score, KPI impacts, and financial ROI. Compare against your ROE Health actuals to see where reality diverges from projections.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──── Section Narrative Component ────
function SectionNarrative({ config }) {
  if (!config) return null;
  return (
    <div className="space-y-1 mb-1">
      {config.subtitle && <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{config.subtitle}</p>}
      <p className="text-xs text-slate-500 leading-relaxed">{config.narrative}</p>
      {config.impact && <p className="text-[10px] text-teal-600 leading-relaxed"><strong>Downstream impact:</strong> {config.impact}</p>}
    </div>
  );
}

const INPUT_INFO = {
  skills_uplift: { what: 'The average % improvement in skill assessment scores from pre-training to post-training.', why: 'This is the primary L2 measure of whether training actually built capability. It directly feeds the Capability (C) component of the People Engine.', source: 'Your LMS assessment reports — compare average pre-test scores vs post-test scores per program. Format: 0-100 scale.' },
  certification_proficiency: { what: 'The % of learners achieving certification or proficiency standards after training.', why: 'Certifications provide objective, validated evidence of capability acquisition — stronger than self-reported learning and more defensible for stakeholders.', source: 'Certification management system or LMS completion reports. Count certified / total enrolled × 100. Format: 0-100%.' },
  knowledge_retention: { what: 'The % of knowledge retained at 30/60/90 days post-training.', why: 'Without retention, capability is temporary. Low retention means you\'re paying for learning that decays before it can drive performance change.', source: 'Post-training knowledge checks administered at 30, 60, or 90 days via LMS or survey tool. Format: average score 0-100%.' },
  engagement_index: { what: 'Combined intrinsic (purpose, mastery, autonomy) and extrinsic (recognition, career, incentives) motivation score.', why: 'Motivation determines whether people choose to apply what they\'ve learned. High capability with low motivation = zero behavior change.', source: 'Employee engagement surveys (e.g., Gallup Q12, Qualtrics Pulse, or custom). Average the intrinsic + extrinsic sub-scores. Format: 0-100%.' },
  confidence_to_apply: { what: 'Learners\' self-reported confidence that they can apply new skills on the job.', why: 'Confidence is the bridge between knowing and doing. Without it, even skilled employees hesitate to change their behavior, blocking ROE.', source: 'Post-training survey question: "How confident are you that you can apply what you learned?" (1-5 Likert scale × 20 to convert to %). Format: 0-100%.' },
  manager_support: { what: 'The degree to which managers actively support and reinforce learning application.', why: 'Manager reinforcement is the #1 predictor of capability transfer. Without it, 80-90% of training doesn\'t translate to behavior change.', source: 'Learner survey: "My manager supports me in applying new skills" (1-5 Likert × 20). Or manager self-assessment survey. Format: 0-100%.' },
  manager_rating: { what: 'Manager assessment of whether observable behavior change has occurred post-training.', why: 'This is the core L3 (Kirkpatrick) measure. It validates that learning actually changed what people do on the job — the critical link to KPI impact.', source: 'Structured manager observation form at 60-90 days post-training: "To what extent has the learner changed their on-the-job behavior?" (1-5 Likert × 20). Format: 0-100%.' },
  behavior_change: { what: 'The degree of observed behavior change measured through structured observation or 360 assessment.', why: 'Behavior change is the observable signal that capability has transferred into the workplace. Without it, ROE cannot be validated and KPI impact remains theoretical.', source: '360 assessment, manager checklists, or structured peer observations at 60-90 days post-training. Average across raters. Format: 0-100%.' },
};

const DIRECT_INPUTS = [
  { section: 'Capability (L2)', icon: Target, color: 'blue', items: [
    { key: 'skills_uplift', label: 'Skills Uplift (Pre → Post)' },
    { key: 'certification_proficiency', label: 'Certification Proficiency' },
    { key: 'knowledge_retention', label: 'Knowledge Retention' },
  ]},
  { section: 'Motivation (L2)', icon: Zap, color: 'amber', items: [
    { key: 'engagement_index', label: 'Engagement Index' },
    { key: 'confidence_to_apply', label: 'Confidence to Apply' },
    { key: 'manager_support', label: 'Manager Support' },
  ]},
  { section: 'Stakeholder Validation (L3)', icon: Users, color: 'purple', items: [
    { key: 'manager_rating', label: 'Manager Rating' },
    { key: 'behavior_change', label: 'Observed Behavior Change' },
  ]},
];

function getColor(pct) {
  if (pct >= 80) return '#06b6d4';
  if (pct >= 55) return '#14b8a6';
  if (pct >= 30) return '#f59e0b';
  return '#ef4444';
}

function ScoreGauge({ value, label, size = 90 }) {
  const color = getColor(value);
  const r = size / 2 - 8;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center"><span className="text-lg font-bold" style={{ color }}>{Math.round(value)}%</span></div>
      </div>
      <span className="text-[10px] text-slate-500 mt-1 text-center leading-tight">{label}</span>
    </div>
  );
}

const sevStyles = {
  Critical: { card: 'border-rose-300 bg-rose-50', text: 'text-rose-800', badge: 'bg-rose-100 text-rose-700 border-rose-200' },
  High: { card: 'border-amber-300 bg-amber-50', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  Moderate: { card: 'border-blue-200 bg-blue-50', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export default function ROEModellerPage() {
  const [mode, setMode] = useState('simple');
  const [results, setResults] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [tab, setTab] = useState('modeller');
  const [baselines, setBaselines] = useState(null);
  const [baselinesLoaded, setBaselinesLoaded] = useState(false);

  // Simple mode: direct values
  const [simpleValues, setSimpleValues] = useState({});
  // Advanced mode: baseline + target pairs
  const [advancedValues, setAdvancedValues] = useState({});

  const [leiSimple, setLeiSimple] = useState({});
  const [leiAdvanced, setLeiAdvanced] = useState({});

  const [cost, setCost] = useState(1000);

  // Load baselines from ROE Health actuals. When the user arrived via
  // the Impact Modelling "Current Opportunity" banner, the leak %
  // (clamped to 1-40) replaces the default +15pt target bump so the
  // pre-filled targets model recovering the detected value leak.
  useEffect(() => {
    const leakParam = parseInt(new URLSearchParams(window.location.search).get('leak'), 10);
    const constraintBump = Number.isFinite(leakParam) && leakParam > 0 ? Math.min(leakParam, 40) : null;
    const bump = constraintBump || 15;
    if (constraintBump) setMode('advanced'); // targets only visible in advanced mode
    api.get('/roe/actuals/health').then(r => {
      if (r.data.has_data) {
        const h = r.data;
        const bl = {
          skills_uplift: h.capability.uplift_avg,
          certification_proficiency: h.capability.cert_avg,
          knowledge_retention: h.capability.retention_avg,
          engagement_index: (h.motivation.intrinsic_avg + h.motivation.extrinsic_avg) / 2,
          confidence_to_apply: h.motivation.confidence_avg,
          manager_support: h.motivation.manager_support_avg,
          manager_rating: h.l3.actual,
          behavior_change: h.l3.actual,
        };
        setBaselines(bl);
        // Pre-populate advanced with baselines
        const adv = {};
        Object.entries(bl).forEach(([k, v]) => {
          adv[k] = { baseline: Math.round(v), target: Math.min(100, Math.round(v) + bump) };
        });
        setAdvancedValues(adv);
        // Pre-populate simple with baseline values
        const sv = {};
        Object.entries(bl).forEach(([k, v]) => { sv[k] = Math.round(v); });
        setSimpleValues(sv);
      } else {
        // Default values
        const defaults = {};
        DIRECT_INPUTS.forEach(s => s.items.forEach(i => { defaults[i.key] = 50; }));
        setSimpleValues(defaults);
        const adv = {};
        DIRECT_INPUTS.forEach(s => s.items.forEach(i => { adv[i.key] = { baseline: 40, target: Math.min(100, 40 + bump) }; }));
        setAdvancedValues(adv);
      }
      setBaselinesLoaded(true);
    }).catch(() => {
      setBaselinesLoaded(true);
    });
  }, []);

  const getPayload = () => {
    let capInputs, motInputs, l3Inputs, leiImps;
    if (mode === 'simple') {
      capInputs = { skills_uplift: simpleValues.skills_uplift || 50, certification_proficiency: simpleValues.certification_proficiency || 50, knowledge_retention: simpleValues.knowledge_retention || 50 };
      motInputs = { engagement_index: simpleValues.engagement_index || 50, confidence_to_apply: simpleValues.confidence_to_apply || 50, manager_support: simpleValues.manager_support || 50 };
      l3Inputs = { manager_rating: simpleValues.manager_rating || 50, behavior_change: simpleValues.behavior_change || 50 };
      leiImps = leiSimple;
    } else {
      // Advanced: use target values, delta = target - baseline
      capInputs = { skills_uplift: advancedValues.skills_uplift?.target || 50, certification_proficiency: advancedValues.certification_proficiency?.target || 50, knowledge_retention: advancedValues.knowledge_retention?.target || 50 };
      motInputs = { engagement_index: advancedValues.engagement_index?.target || 50, confidence_to_apply: advancedValues.confidence_to_apply?.target || 50, manager_support: advancedValues.manager_support?.target || 50 };
      l3Inputs = { manager_rating: advancedValues.manager_rating?.target || 50, behavior_change: advancedValues.behavior_change?.target || 50 };
      leiImps = {};
      Object.entries(leiAdvanced).forEach(([k, v]) => { leiImps[k] = Math.max(0, (v.target || 0) - (v.baseline || 0)); });
    }
    return {
      lei_improvements: leiImps,
      capability_inputs: capInputs,
      motivation_inputs: motInputs,
      l3_inputs: l3Inputs,
      financial: { intervention_cost: cost },
      overrides: {},
      baseline_pe: 0.5,
    };
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/roe/submit', getPayload());
      setResults(res.data);
      setNarrative(null);
      setTab('results');
      toast.success(`ROE: ${res.data.roe.pct}% (${res.data.roe.band})`);
    } catch (err) { logger.error('ROE calc failed:', err); toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleNarrative = async () => {
    setNarrativeLoading(true);
    try { const res = await api.post('/roe/narrative', { results }); setNarrative(res.data.narrative); }
    catch { toast.error('Failed'); }
    finally { setNarrativeLoading(false); }
  };

  if (!baselinesLoaded) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;

  const __pageTheme = getLightSectionTheme('ROE');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Transfer Modeller"
        description={
          <>
            Model how a planned learning intervention flows through the <strong className="text-slate-900">Learning ROI Waterfall</strong> — from training improvement to validated workforce capacity to financial return.
            {baselines
              ? <span className="block mt-1 text-slate-700">Baselines loaded from actual ROE health data.</span>
              : <span className="block mt-1 text-slate-700">No actuals data — using default baselines.</span>}
          </>
        }
        theme={__pageTheme}
        icon={ClipboardCheck}
        testId="roe-modeller-hero"
        actions={(
          <Link
            to="/roe-reference#waterfall"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-xs font-medium backdrop-blur-sm transition-colors"
            data-testid="roe-modeller-hero-reference-btn"
          >
            <BookOpen className="h-3.5 w-3.5" /> ROE Reference
          </Link>
        )}
      >
        {/* Inline Learning ROI Waterfall flow inside the hero so
            the seven-stage chain is the first thing the user sees,
            replacing the duplicate header that used to sit below
            the hero band. */}
        <div
          className="flex items-center gap-1.5 sm:gap-2 mt-4 p-3 bg-white border border-slate-200 backdrop-blur-sm rounded-lg overflow-x-auto"
          data-testid="roe-flow-diagram"
        >
          {['LEI', 'C × M', 'PE Ideal', 'ROE', 'PE Validated', 'KPI', 'ROI'].map((step, i) => (
            <React.Fragment key={step}>
              <div className={cn(
                "px-2.5 py-1 rounded-lg text-xs font-bold whitespace-nowrap",
                step === 'ROE' ? 'bg-white text-slate-900 shadow-md'
                  : step === 'PE Validated' ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-900'
              )}>{step}</div>
              {i < 6 && <ArrowRight className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </PageHero>
    )}>
    <div className="-mt-2 md:-mt-4 space-y-4" data-testid="roe-modeller-page">
      <ConstraintContextBanner hint="Targets below are pre-set to recover the detected leak — adjust as needed before running the model." />
      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="mode-toggle">
          <button onClick={() => setMode('simple')} className={cn("flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all",
            mode === 'simple' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          )} data-testid="mode-simple"><SlidersHorizontal className="h-3.5 w-3.5" /> Simple</button>
          <button onClick={() => setMode('advanced')} className={cn("flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all",
            mode === 'advanced' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          )} data-testid="mode-advanced"><Table2 className="h-3.5 w-3.5" /> Advanced</button>
        </div>
        <span className="text-xs text-slate-400">{mode === 'simple' ? 'Move sliders to set expected values' : 'Enter current baseline + target improvement'}</span>
        {baselines && mode === 'advanced' && (
          <Badge variant="outline" className="text-xs text-teal-600 border-teal-200 ml-auto">Baselines from actuals</Badge>
        )}
      </div>

      {tab === 'modeller' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: LEI + How-it-works accordion */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">LEI Measure Improvements</CardTitle></CardHeader>
              <CardContent>
                <SectionNarrative config={{ narrative: mode === 'simple' ? SECTION_NARRATIVES.lei.simple : SECTION_NARRATIVES.lei.advanced }} />
                <div className={cn("space-y-3 pr-1", mode === 'advanced' ? 'max-h-[560px] overflow-y-auto' : '')}>
                  {LEI_MEASURES.filter(m => mode === 'advanced' || SIMPLE_IDS.includes(m.id)).map(m => (
                    mode === 'simple' ? (
                      <SimpleSliderWithInfo key={m.id} label={m.label} value={leiSimple[m.id] || 0} onChange={v => setLeiSimple(prev => ({ ...prev, [m.id]: v }))} testId={`lei-${m.id}`}
                        info={{ what: m.info, why: 'LEI improvements flow through the C/M mapping to drive the People Engine score.', source: m.source }} />
                    ) : (
                      <BaselineTargetRow key={m.id} label={m.label} baseline={leiAdvanced[m.id]?.baseline || 0} target={leiAdvanced[m.id]?.target || 0}
                        onChange={(b, t) => setLeiAdvanced(prev => ({ ...prev, [m.id]: { baseline: b, target: t } }))} testId={`lei-${m.id}`}
                        info={{ what: m.info, why: 'LEI improvements flow through the C/M mapping to drive the People Engine score.', source: m.source }} />
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
            <CauseEffectDiagram />
          </div>

          {/* Right: Direct inputs */}
          <div className="space-y-4">
            {DIRECT_INPUTS.map(({ section, icon: Icon, color, items }) => {
              const narrativeKey = section === 'Capability (L2)' ? 'capability' : section === 'Motivation (L2)' ? 'motivation' : 'l3';
              return (
                <Card key={section}>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Icon className={cn("h-4 w-4", `text-${color}-500`)} /> {section}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <SectionNarrative config={SECTION_NARRATIVES[narrativeKey]} />
                    {items.map(item => (
                      mode === 'simple' ? (
                        <SimpleSliderWithInfo key={item.key} label={item.label} value={simpleValues[item.key] || 50} onChange={v => setSimpleValues(prev => ({ ...prev, [item.key]: v }))} testId={item.key}
                          info={INPUT_INFO[item.key]} />
                      ) : (
                        <BaselineTargetRow key={item.key} label={item.label} baseline={advancedValues[item.key]?.baseline || 0} target={advancedValues[item.key]?.target || 0}
                          onChange={(b, t) => setAdvancedValues(prev => ({ ...prev, [item.key]: { baseline: b, target: t } }))} testId={item.key}
                          info={INPUT_INFO[item.key]} />
                      )
                    ))}
                  </CardContent>
                </Card>
              );
            })}

            <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-teal-600" /> Financial</CardTitle></CardHeader><CardContent>
              <SectionNarrative config={SECTION_NARRATIVES.financial} />
              <div className="flex items-center justify-between"><Label className="text-xs">Cost ($/employee)</Label><input type="number" value={cost} onChange={e => setCost(Number(e.target.value))} className="w-24 text-right text-sm border rounded px-2 py-1" data-testid="intervention-cost" /></div>
            </CardContent></Card>

            <Button onClick={handleCalculate} disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="calculate-roe-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />} Calculate ROE
            </Button>
          </div>
        </div>
      )}

      {tab === 'results' && results && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-semibold text-slate-800">Modelled Results</h3>
            <Button variant="outline" size="sm" onClick={() => setTab('modeller')} data-testid="back-to-inputs"><ArrowRight className="h-3.5 w-3.5 mr-1 rotate-180" /> Modify Inputs</Button>
          </div>
          <ResultsView results={results} narrative={narrative} onNarrative={handleNarrative} narrativeLoading={narrativeLoading} />
        </div>
      )}
    </div>
    </PageShell>
  );
}

// ──── Slider with Info ────
function SimpleSliderWithInfo({ label, value, onChange, testId, info }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-slate-600">{label}</Label>
          {info && <button onClick={() => setShowInfo(!showInfo)} className="text-slate-400 hover:text-indigo-500 transition-colors" data-testid={`${testId}-info`}><Info className="h-3.5 w-3.5" /></button>}
        </div>
        <span className="text-xs font-mono font-bold text-slate-700">{value}%</span>
      </div>
      {showInfo && info && (
        <div className="p-2 bg-indigo-50 border border-indigo-100 rounded-md text-xs leading-relaxed space-y-1">
          <p className="text-indigo-800"><strong>What this is:</strong> {info.what}</p>
          <p className="text-indigo-700"><strong>Why it matters:</strong> {info.why}</p>
          {info.source && <p className="text-indigo-600"><strong>Data source & format:</strong> {info.source}</p>}
        </div>
      )}
      <Slider value={[value]} onValueChange={([v]) => onChange(v)} min={0} max={100} step={1} data-testid={testId} />
    </div>
  );
}

// ──── Baseline + Target Row ────
function BaselineTargetRow({ label, baseline, target, onChange, testId, info }) {
  const [showInfo, setShowInfo] = useState(false);
  const delta = target - baseline;
  return (
    <div className="p-2.5 rounded-lg border border-slate-100 hover:border-slate-200 transition-all" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-2">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        {info && (
          <button onClick={() => setShowInfo(!showInfo)} className="text-slate-400 hover:text-indigo-500 transition-colors" data-testid={`${testId}-info`}>
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {showInfo && info && (
        <div className="mb-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-md text-xs leading-relaxed space-y-1">
          <p className="text-indigo-800"><strong>What to enter:</strong> {info.what}</p>
          <p className="text-indigo-700"><strong>Why it matters:</strong> {info.why}</p>
          {info.source && <p className="text-indigo-600"><strong>Data source & format:</strong> {info.source}</p>}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 items-center">
        <div>
          <Label className="text-[10px] text-slate-400">Current</Label>
          <input type="number" value={baseline} onChange={e => onChange(Number(e.target.value), target)} min={0} max={100}
            className="w-full text-center text-sm border rounded px-1.5 py-1 bg-slate-50" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-400">Target</Label>
          <input type="number" value={target} onChange={e => onChange(baseline, Number(e.target.value))} min={0} max={100}
            className="w-full text-center text-sm border rounded px-1.5 py-1 bg-white border-teal-200" />
        </div>
        <div className="text-center">
          <Label className="text-[10px] text-slate-400">Delta</Label>
          <p className={cn("text-sm font-bold", delta > 0 ? 'text-teal-600' : delta < 0 ? 'text-rose-500' : 'text-slate-400')}>
            {delta > 0 ? '+' : ''}{delta}%
          </p>
        </div>
      </div>
    </div>
  );
}

// ──── Results View ────
function ResultsView({ results, narrative, onNarrative, narrativeLoading }) {
  const { capability, motivation, people_engine, l2_learner_performance_shift, l3_stakeholder_impact, roe, kpi_impacts, financial, risks } = results;
  const [savingTargets, setSavingTargets] = useState(false);
  const [targetsSaved, setTargetsSaved] = useState(false);
  const saveAsTargets = async () => {
    setSavingTargets(true);
    try {
      await api.post('/roe/actuals/targets', {
        targets: {
          capability: capability?.pct,
          motivation: motivation?.pct,
          l3: l3_stakeholder_impact?.pct,
          roe: roe?.pct,
        },
      });
      setTargetsSaved(true);
      toast.success('Targets saved — Actuals cards will show "vs target" chips.');
    } catch (err) {
      logger.error('save targets failed', err);
      toast.error('Could not save targets.');
    } finally {
      setSavingTargets(false);
    }
  };
  const waterfallSteps = [
    { name: 'C', value: capability.pct, fill: '#3b82f6' },
    { name: 'M', value: motivation.pct, fill: '#f59e0b' },
    { name: 'PE Ideal', value: people_engine.ideal_pct, fill: '#8b5cf6' },
    { name: 'L2', value: l2_learner_performance_shift.pct, fill: '#06b6d4' },
    { name: 'L3', value: l3_stakeholder_impact.pct, fill: '#10b981' },
    { name: 'ROE', value: roe.pct, fill: roe.band_color },
    { name: 'PE Valid', value: people_engine.validated_pct, fill: '#7c3aed' },
  ];

  return (
    <>
      <Card className="border-indigo-200 bg-indigo-50/50" data-testid="roe-impact-card">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-indigo-700/80">Projected impact — capability → behavior → ROE</p>
            <Button
              size="sm" variant="outline"
              onClick={saveAsTargets} disabled={savingTargets}
              className="h-7 shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs"
              data-testid="roe-save-targets-btn"
              title="Save these component scores as targets — the Actuals cards will show a vs-target chip"
            >
              {savingTargets ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Target className="h-3.5 w-3.5 mr-1.5" />}
              {targetsSaved ? 'Targets saved' : 'Save as targets'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600" data-testid="roe-impact-capability">Capability <span className="font-bold text-indigo-700">{results.capability?.pct}%</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600" data-testid="roe-impact-motivation">Motivation <span className="font-bold text-indigo-700">{results.motivation?.pct}%</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600" data-testid="roe-impact-behavior">Behavior (L3) <span className="font-bold text-indigo-700">{l3_stakeholder_impact.pct}%</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600" data-testid="roe-impact-roe">ROE <span className="font-bold text-teal-700">{roe.pct}%</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600" data-testid="roe-impact-pe-delta">PE Δ vs baseline <span className="font-bold text-purple-700">{people_engine.delta >= 0 ? '+' : ''}{Math.round((people_engine.delta || 0) * 100)}%</span></span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-0 shadow-lg" style={{ background: `linear-gradient(135deg, ${roe.band_color}, ${roe.band_color}dd)` }}>
          <CardContent className="pt-4 pb-3 text-center text-slate-700"><p className="text-[10px] uppercase opacity-70 font-semibold">ROE</p><p className="text-3xl font-extrabold">{roe.pct}%</p><Badge className="bg-white border border-slate-200 text-slate-900 text-xs">{roe.band}</Badge></CardContent>
        </Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-[10px] text-slate-500 uppercase">PE Ideal</p><p className="text-2xl font-bold text-purple-500">{people_engine.ideal_pct}%</p></CardContent></Card>
        <Card className="border-purple-200"><CardContent className="pt-4 pb-3 text-center"><p className="text-[10px] text-slate-500 uppercase">PE Validated</p><p className="text-2xl font-bold text-purple-700">{people_engine.validated_pct}%</p><p className="text-[10px] text-slate-400">gap: {people_engine.transfer_gap_pct}%</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-[10px] text-slate-500 uppercase">Value/Emp</p><p className="text-2xl font-bold text-teal-600">${financial.total_value_per_employee}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-[10px] text-slate-500 uppercase">ROI</p><p className={cn("text-2xl font-bold", financial.roi_pct >= 0 ? 'text-emerald-600' : 'text-rose-500')}>{financial.roi_pct}%</p></CardContent></Card>
      </div>

      {people_engine.transfer_gap_pct > 5 && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div><p className="text-sm font-semibold text-purple-800">Transfer Gap: {people_engine.transfer_gap_pct}%</p><p className="text-sm text-purple-700">PE Ideal {people_engine.ideal_pct}% → PE Validated {people_engine.validated_pct}%.</p></div>
        </div>
      )}

      {risks?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-500" /> Risk Analysis</CardTitle></CardHeader><CardContent className="space-y-2">
          {risks.map(r => (<div key={r.area} className={cn("p-3 rounded-lg border", sevStyles[r.severity]?.card)}><div className="flex items-center gap-2 mb-1"><Badge className={cn("text-xs", sevStyles[r.severity]?.badge)}>{r.severity}</Badge><span className={cn("text-sm font-semibold", sevStyles[r.severity]?.text)}>{r.area}</span><span className="text-xs text-slate-400 ml-auto">{r.score}%</span></div><p className="text-sm text-slate-700">{r.message}</p><p className="text-xs text-slate-500 mt-1"><strong>Action:</strong> {r.action}</p></div>))}
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Value Chain: Ideal → Validated</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={waterfallSteps}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 10 }} /><Tooltip formatter={v => `${v}%`} /><Bar dataKey="value" radius={[4,4,0,0]}>{waterfallSteps.map((s,i) => <Cell key={`c-${i}`} fill={s.fill} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">C × M = PE × ROE = Validated</CardTitle></CardHeader><CardContent>
          <div className="flex items-center justify-center gap-3 py-4 flex-wrap">
            <ScoreGauge value={capability.pct} label="C" /><span className="text-lg font-bold text-slate-300">x</span>
            <ScoreGauge value={motivation.pct} label="M" /><span className="text-lg font-bold text-slate-300">=</span>
            <ScoreGauge value={people_engine.ideal_pct} label="PE Ideal" /><span className="text-lg font-bold text-slate-300">x</span>
            <ScoreGauge value={roe.pct} label="ROE" /><span className="text-lg font-bold text-slate-300">=</span>
            <ScoreGauge value={people_engine.validated_pct} label="PE Valid" />
          </div>
        </CardContent></Card>
      </div>

      <Card><CardHeader className="pb-2"><CardTitle className="text-sm">KPI Impact</CardTitle></CardHeader><CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={kpi_impacts.map(k => ({ name: k.kpi_name.split(' ')[0], impact: k.impact_pct, value: k.value_per_employee }))} margin={{ bottom: 15 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Legend /><Bar dataKey="impact" name="Impact %" fill="#14b8a6" radius={[4,4,0,0]} /><Bar dataKey="value" name="$/Emp" fill="#3b82f6" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent></Card>

      <Card><CardContent className="pt-4">
        {narrative ? (
          <div><h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3"><Brain className="h-4 w-4 text-indigo-600" /> AI Analysis</h4>
            <div className="text-sm text-slate-600 leading-relaxed space-y-2">{narrative.split('\n\n').filter(p => p.trim()).map((p,i) => <p key={`n-${i}`}>{p}</p>)}</div></div>
        ) : (
          <div className="text-center py-4"><Button onClick={onNarrative} disabled={narrativeLoading} variant="outline" data-testid="roe-narrative-btn">
            {narrativeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />} Generate AI Analysis</Button></div>
        )}
      </CardContent></Card>
    </>
  );
}
